import { useFormContext, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { ClassificationDtoIn } from '@/models/in/ClassificationDtoIn';
import type { SupportedFormData } from './schemas/supportedFormSchema';

interface ClassifictionsSectionProps {
  /**
   * HAT-1464: classifications a deleted supported entered again already holds in this fund. Shown
   * as existing and not selectable - the server refuses a second one of the same type.
   */
  existingClassificationIds?: number[];
}

const ClassifictionsSection = ({ existingClassificationIds = [] }: ClassifictionsSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<SupportedFormData>();
  const { data: classifications } = useGetAuthApi<ClassificationDtoIn[]>('classifications');
  
  const { fields: contactClassifications, append, remove } = useFieldArray({
    control,
    name: 'contactClassifications'
  });

  const handleClassificationToggle = (classificationId: number) => {
    const isSelected = contactClassifications.some((c) => c.classificationId === classificationId);

    if (isSelected) {
      // Remove the classification
      const index = contactClassifications.findIndex((c) => c.classificationId === classificationId);
      if (index !== -1) {
        remove(index);
      }
    } else {
      // Add the classification
      append({
        id: 0,
        classificationId: classificationId,
        requiredDocuments: [],
        additionalDocuments: [],
        comments: '',
      });
    }
  };

  return (
    <div className="flex flex-col gap-6 w-[700px] max-md:w-full">
      <div className="flex flex-col gap-2">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('supportGoalsTitle')}</h4>
        <p className="body-sm-regular text-(--font-body-3)">{t('fundSelectionSubtitle')}</p>
      </div>

      <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1 max-md:gap-4">
        {(classifications ?? []).map((classification) => {
          const isSelected = contactClassifications.some((c) => c.classificationId === classification.id);
          const isExisting = existingClassificationIds.includes(classification.id);
          return (
            <label
              key={classification.id}
              className={`flex flex-col gap-3 p-6 max-md:p-4 rounded-xl border transition-colors ${
                isExisting
                  ? 'bg-(--surface-grey-lowest) border-(--border-lowest) cursor-not-allowed'
                  : isSelected
                    ? 'bg-(--surface-secondary-lowest) border-(--border-secondary-strong) cursor-pointer'
                    : 'bg-(--surface-base) border-(--border-main) cursor-pointer'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center justify-center shrink-0 gap-2">
                  <div className="flex flex-col items-start gap-1">
                    <h5 className="body-md-semibold text-(--font-body-1)">{t(classification.name)}</h5>
                    {isExisting && <span className="body-sm-regular text-(--font-body-3)">{t('classificationAlreadyExistsForSupported')}</span>}
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={isExisting}
                    onChange={() => handleClassificationToggle(classification.id)}
                    className="appearance-none w-5 h-5 rounded-[10px] border transition-colors cursor-pointer
                      checked:bg-(--border-secondary-strong) checked:border-(--border-secondary-strong)
                      unchecked:bg-(--surface-base) unchecked:border-(--border-low)
                      relative
                      checked:after:content-[''] checked:after:absolute checked:after:inset-[3px]
                      checked:after:bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMTQiIHZpZXdCb3g9IjAgMCAxNCAxNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTExLjY2NjcgMy41TDUuMjUgOS45MTY2N0wyLjMzMzM3IDciIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=')]
                      checked:after:bg-contain checked:after:bg-center checked:after:bg-no-repeat"
                  />
                </div>
                
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default ClassifictionsSection;
